import { KeyValueGrid } from "@/pages/tina/items/components/key-value-grid";
import { PanelShell } from "@/pages/tina/items/components/panel-shell";
import {
  describeSkippedReason,
  isExpectedSkip,
} from "@/pages/tina/items/constants";
import type { PipelineItem } from "@/types/tina";

interface ResolvedSkippedReason {
  code: string | null;
  label: string;
  source: string | null;
  isMissing: boolean;
}

export function SkippedPanel({ item }: { item: PipelineItem }) {
  const reason = resolveSkippedReason(item);
  const stage = item.current_stage ?? "—";
  const expected = isExpectedSkip(reason.code);

  return (
    <PanelShell
      status="skipped"
      subtitle={
        reason.isMissing
          ? `Pipeline stopped at ${stage}; the skip code was not persisted.`
          : expected
            ? `${stage} had nothing to do — this is the expected path, not a failure.`
            : `Pipeline stopped at ${stage}.`
      }
    >
      <div className="rounded-md border bg-muted/20 p-3">
        <p className="text-sm font-medium leading-relaxed">{reason.label}</p>
        {expected && (
          <p className="mt-2 text-xs leading-relaxed text-emerald-700 dark:text-emerald-400">
            Nothing to investigate. The work this job asked for had already been
            done (or was never requested), so it terminated without spending
            anything.
          </p>
        )}
        {reason.isMissing && (
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            Tina marked this item as skipped without storing a reason code. Open
            the {stage} stage in Stage executions and inspect the artifacts for
            the extractor output.
          </p>
        )}
      </div>
      <KeyValueGrid
        rows={[
          { label: "Stage", value: stage, mono: true },
          {
            label: "Reason code",
            value: reason.code ?? "missing",
            mono: Boolean(reason.code),
          },
          {
            label: "Reason source",
            value: reason.source ?? "—",
            mono: Boolean(reason.source),
          },
          {
            label: "Outcome",
            value: item.final_outcome ?? "—",
            mono: Boolean(item.final_outcome),
          },
        ]}
      />
    </PanelShell>
  );
}

function resolveSkippedReason(item: PipelineItem): ResolvedSkippedReason {
  const itemReason = normalizeReason(item.skipped_reason);
  if (itemReason) {
    return knownReason(itemReason, "item.skipped_reason");
  }

  for (const candidate of metadataReasonCandidates(item)) {
    const reason = normalizeReason(candidate.value);
    if (reason) return knownReason(reason, candidate.source);
  }

  const errorReason = normalizeReason(item.error?.reason ?? item.error?.code);
  if (errorReason) return knownReason(errorReason, "item.error");

  return {
    code: null,
    label: "Skip reason was not persisted.",
    source: null,
    isMissing: true,
  };
}

function knownReason(code: string, source: string): ResolvedSkippedReason {
  return {
    code,
    label: describeSkippedReason(code),
    source,
    isMissing: false,
  };
}

function metadataReasonCandidates(item: PipelineItem) {
  const metadata = item.metadata ?? {};
  const keys = uniqueStrings([
    item.current_stage ? `${item.current_stage}_skipped_reason` : null,
    "extract_skipped_reason",
    "venue_resolution_skipped_reason",
    "artist_resolution_skipped_reason",
    "person_resolution_skipped_reason",
    "movie_materialization_skipped_reason",
    "play_materialization_skipped_reason",
    "skipped_reason",
    ...Object.keys(metadata).filter((key) => key.endsWith("_skipped_reason")),
  ]);

  return keys.map((key) => ({
    source: `metadata.${key}`,
    value: metadata[key],
  }));
}

function uniqueStrings(values: Array<string | null>): string[] {
  return Array.from(
    new Set(values.filter((value): value is string => !!value)),
  );
}

function normalizeReason(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}
